import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { cn } from 'lib/utils'

export const AdasMenu = ({
  open,
  onClose,
  anchorEl,
  children,
  className,
  sx,
  transformOrigin,
  anchorOrigin,
  options,
  handleMenuItemClick,
  PaperProps,
  keepMounted,
  ...props
}) => {
  const [style, setStyle] = useState({})
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open || !anchorEl) return
    const el = typeof anchorEl === 'function' ? anchorEl() : anchorEl
    const rect = el?.getBoundingClientRect?.() || el?.current?.getBoundingClientRect?.()
    if (!rect) return

    // Determine anchor point based on anchorOrigin
    const anchorV = anchorOrigin?.vertical || 'bottom'
    const anchorH = anchorOrigin?.horizontal || 'left'
    const anchorTop = anchorV === 'top' ? rect.top : anchorV === 'center' ? rect.top + rect.height / 2 : rect.bottom
    const anchorLeft = anchorH === 'right' ? rect.right : anchorH === 'center' ? rect.left + rect.width / 2 : rect.left

    // Determine transform offset based on transformOrigin
    const transformH = transformOrigin?.horizontal || 'left'
    const translateX = transformH === 'right' ? '-100%' : transformH === 'center' ? '-50%' : '0%'

    setStyle({
      position: 'fixed',
      top: anchorTop + 4,
      left: anchorLeft,
      transform: `translateX(${translateX})`,
      zIndex: 1300,
      minWidth: rect.width
    })
  }, [open, anchorEl, anchorOrigin, transformOrigin])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    const handleClickOutside = (e) => {
      const el = typeof anchorEl === 'function' ? anchorEl() : anchorEl
      const anchor = el?.current || el
      if (anchor && !anchor.contains(e.target) && !menuRef.current?.contains(e.target)) onClose?.()
    }
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose, anchorEl])

  if (!open) return null
  return createPortal(
    <div
      ref={menuRef}
      style={style}
      className={cn('bg-white rounded-card shadow-xl border border-custom-border py-1', className)}
      {...props}
    >
      {options
        ? options.map(
            (option, index) =>
              !!option.visible && (
                <button
                  key={index}
                  disabled={option.disabled}
                  onClick={() => handleMenuItemClick && handleMenuItemClick(option.action)}
                  className='flex w-full items-center px-4 py-2 text-sm text-left hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {option.label}
                </button>
              )
          )
        : children}
    </div>,
    document.body
  )
}

AdasMenu.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  children: PropTypes.node,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      visible: PropTypes.bool,
      action: PropTypes.func,
      label: PropTypes.string
    })
  ),
  handleMenuItemClick: PropTypes.func
}
